import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import Select, { components } from "react-select";
import {
  fetchResourceUtilFilters,
  fetchResourceUtilResult,
} from "../../../API_Service/Dashboard_Slice";
import { companyID, employeeID, DashboardCard, ChartSkeleton, InlineAlert } from "../../../Common/UIComponents";
import { CHART_PALETTE, axisLabelStyle, chartChrome } from "../../../Common/chartTheme";

const COLORS = CHART_PALETTE;

const ALL_OPTION = { value: "__ALL__", label: "Select All" };

/* ---- Collapse chips after the first one: "Facility A  +3 more" ---- */
const CollapsedMultiValue = ({ index, getValue, ...props }) => {
  const values = getValue();
  const maxToShow = 1;
  if (index < maxToShow) return <components.MultiValue {...props} />;
  return index === maxToShow ? (
    <div style={{ fontSize: 12, color: "#546E7A", marginLeft: 4 }}>
      +{values.length - maxToShow} more
    </div>
  ) : null;
};

/* ---- Compact styles so the control doesn't sprawl ---- */
const compactSelectStyles = {
  container: (base) => ({ ...base, width: 240 }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

/**
 * Multi-select with a "Select All" pseudo-option.
 * Fires onChange(ids: number[]) immediately on every change —
 * no separate Generate button needed.
 */
function MultiSelectWithAll({ options, value, onChange, placeholder }) {
  const allSelected = options.length > 0 && value.length === options.length;

  const selectOptions = [ALL_OPTION, ...options];
  const selectedValues = allSelected
    ? [ALL_OPTION, ...options]
    : options.filter((o) => value.includes(o.value));

  const handleChange = (selected, actionMeta) => {
    switch (actionMeta.action) {
      case "select-option":
        if (actionMeta.option?.value === ALL_OPTION.value) {
          onChange(options.map((o) => o.value));
          return;
        }
        break;
      case "deselect-option":
        if (actionMeta.option?.value === ALL_OPTION.value) {
          onChange([]);
          return;
        }
        break;
      case "remove-value":
        if (actionMeta.removedValue?.value === ALL_OPTION.value) {
          onChange([]);
          return;
        }
        break;
      case "clear":
        onChange([]);
        return;
      default:
        break;
    }
    onChange(
      (selected || [])
        .filter((s) => s.value !== ALL_OPTION.value)
        .map((s) => s.value),
    );
  };

  return (
    <Select
      isMulti
      options={selectOptions}
      value={selectedValues}
      onChange={handleChange}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      styles={compactSelectStyles}
      components={{ MultiValue: CollapsedMultiValue }}
    />
  );
}

const barOptions = (categories, columnWidth = "65%") => ({
  chart: { ...chartChrome, type: "bar", height: 350, toolbar: { show: true } },
  colors: COLORS,
  plotOptions: {
    bar: {
      columnWidth,
      horizontal: false,
      distributed: true,
      dataLabels: { position: "center" },
    },
  },
  dataLabels: { enabled: true },
  legend: { show: false },
  xaxis: {
    categories,
    labels: {
      style: axisLabelStyle,
    },
  },
});

const ChartPair = ({ hoursOptions, hoursSeries, projOptions, projSeries }) => (
  <div className="flex flex-wrap gap-4">
    <div className="flex-1 basis-[48%] min-w-[280px]">
      <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">No of Hours</p>
      <div className="chart-fade-in">
        <Chart
          options={hoursOptions}
          series={hoursSeries}
          type="bar"
          height={350}
        />
      </div>
    </div>
    <div className="flex-1 basis-[48%] min-w-[280px]">
      <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">No of Projects</p>
      <div className="chart-fade-in">
        <Chart
          options={projOptions}
          series={projSeries}
          type="bar"
          height={350}
        />
      </div>
    </div>
  </div>
);

export default function ResourceUtilization() {
  const dispatch = useDispatch();

  const currentYear = new Date().getFullYear();
  const years = [
    { value: 0, label: "ALL" },
    ...Array.from({ length: currentYear - 2018 }, (_, i) => {
      const y = currentYear - i;
      return { value: y, label: String(y) };
    }),
  ];
  const months = [
    { value: 0, label: "MONTH" },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: String(i + 1),
    })),
  ];

  const [filters, setFilters] = useState({
    facilities: [],
    testTypes: [],
    employees: [],
  });

  const [filterState, setFilterState] = useState({
    role: "TO",
    year: 0,
    month: 0,
    employeeId: employeeID,
    facilityIds: [],
    testTypeIds: [],
    type: "TESTTYPE",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(fetchResourceUtilFilters({ companyId: companyID }))
      .unwrap()
      .then((res) => {
        setFilters({
          facilities: res.facilities ?? [],
          testTypes: res.testTypes ?? [],
          employees: res.employees ?? [],
        });
      })
      .catch((err) => setError(err?.message || "Failed to load filters"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReport = (override = {}) => {
    const f = { ...filterState, ...override };
    setLoading(true);
    dispatch(
      fetchResourceUtilResult({
        EmpId: f.employeeId,
        employeeid: f.employeeId,
        selTestOfficer: f.role,
        TestTypeid: f.testTypeIds.length ? f.testTypeIds.join(",") : "null",
        FacilityID: f.facilityIds.length ? f.facilityIds.join(",") : "null",
        BusinessUnitID: f.businessUnitIds?.length
          ? f.businessUnitIds.join(",")
          : "null",
        Year: f.year,
        Month: f.month,
        type: f.type,
        CompanyID: companyID,
      }),
    )
      .unwrap()
      .then((res) => setData(res))
      .catch((err) => setError(err?.message || "Failed to load chart data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.role,
    filterState.year,
    filterState.month,
    filterState.employeeId,
    filterState.type,
  ]);

  /* Auto-fetch as soon as facility / test type selection changes */
  const handleFacilityChange = (ids) => {
    setFilterState((p) => ({ ...p, facilityIds: ids }));
    runReport({ facilityIds: ids });
  };

  const handleTestTypeChange = (ids) => {
    setFilterState((p) => ({ ...p, testTypeIds: ids }));
    runReport({ testTypeIds: ids });
  };

  const testTypeHours = useMemo(() => {
    const rows = data?.lstTesttype ?? [];
    return {
      categories: rows.map((r) => r.TestTypeNamess),
      series: [{ name: "TOTAL HOURS", data: rows.map((r) => r.TotHrs) }],
    };
  }, [data]);

  const testTypeProj = useMemo(() => {
    const rows = data?.lstProjCntTesttype ?? [];
    return {
      categories: rows.map((r) => r.TestTypeNamess),
      series: [{ name: "Project Count", data: rows.map((r) => r.NoofProj) }],
    };
  }, [data]);

  const facilityHours = useMemo(() => {
    const rows = data?.lstFacilityName ?? [];
    return {
      categories: rows.map((r) => r.Facilitys),
      series: [{ name: "Total Hours", data: rows.map((r) => r.TotHrs) }],
    };
  }, [data]);

  const facilityProj = useMemo(() => {
    const rows = data?.lstProjCntFacilityName ?? [];
    return {
      categories: rows.map((r) => r.Facilitys),
      series: [{ name: "Project Count", data: rows.map((r) => r.NoofProj) }],
    };
  }, [data]);

  const selectClass =
    "px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all";

  return (
    <DashboardCard
      icon="engineering"
      title="Resource Utilization"
      actions={
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={filterState.type === "TESTTYPE"}
            onChange={(e) =>
              setFilterState((p) => ({
                ...p,
                type: e.target.checked ? "TESTTYPE" : "ASSOCIATE",
              }))
            }
          />
          {filterState.type === "TESTTYPE"
            ? "Test Type wise"
            : "Associate wise"}
        </label>
      }
    >
      <div className="flex gap-2.5 flex-wrap mb-4">
        <select
          className={selectClass}
          style={{ width: 100 }}
          value={filterState.role}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, role: e.target.value }))
          }
        >
          <option value="TO">TO</option>
          <option value="TO1">TO1</option>
          <option value="PR">PR</option>
        </select>

        <select
          className={selectClass}
          style={{ width: 90 }}
          value={filterState.year}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, year: Number(e.target.value) }))
          }
        >
          {years.map((y) => (
            <option key={y.value} value={y.value}>
              {y.label}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          style={{ width: 95 }}
          value={filterState.month}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, month: Number(e.target.value) }))
          }
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        {filterState.type === "TESTTYPE" && (
          <select
            className={selectClass}
            style={{ width: 200 }}
            value={filterState.employeeId}
            onChange={(e) =>
              setFilterState((p) => ({
                ...p,
                employeeId: Number(e.target.value),
              }))
            }
          >
            <option value={0}>Select Employee</option>
            {filters.employees.map((emp) => (
              <option key={emp.Value} value={emp.Value}>
                {emp.Text}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <InlineAlert type="error" message={error} />}
      {loading && <ChartSkeleton height={160} />}

      {/* Facility section */}
      <div className="bg-slate-50 rounded-lg border border-slate-200/60 p-4 mb-4">
        <div className="mb-3">
          <MultiSelectWithAll
            options={filters.facilities.map((f) => ({
              value: f.Value,
              label: f.Text,
            }))}
            value={filterState.facilityIds}
            onChange={handleFacilityChange}
            placeholder="Select Facility"
          />
        </div>
        <ChartPair
          hoursOptions={barOptions(facilityHours.categories)}
          hoursSeries={facilityHours.series}
          projOptions={barOptions(facilityProj.categories)}
          projSeries={facilityProj.series}
        />
      </div>

      {/* Test Type section */}
      <div className="bg-slate-50 rounded-lg border border-slate-200/60 p-4">
        <div className="mb-3">
          <MultiSelectWithAll
            options={filters.testTypes.map((t) => ({
              value: t.Value,
              label: t.Text,
            }))}
            value={filterState.testTypeIds}
            onChange={handleTestTypeChange}
            placeholder="Select Test Type"
          />
        </div>
        <ChartPair
          hoursOptions={barOptions(testTypeHours.categories)}
          hoursSeries={testTypeHours.series}
          projOptions={barOptions(testTypeProj.categories)}
          projSeries={testTypeProj.series}
        />
      </div>
    </DashboardCard>
  );
}
